import { useId, useMemo, useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { useFeatureReveal } from "@/hooks/use-feature-reveal";
import { cn } from "@/lib/utils";

interface AdvancedSectionProps {
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  defaultOpen?: boolean;
  description?: string;
  title?: string;
  autoExpandAfterDays?: number;
}

export function AdvancedSection({
  children,
  className,
  contentClassName,
  defaultOpen = false,
  description,
  title = "Advanced",
  autoExpandAfterDays = 14,
}: AdvancedSectionProps) {
  const { daysSinceFirstLogin } = useFeatureReveal();
  const defaultExpanded = useMemo(
    () => defaultOpen || daysSinceFirstLogin >= autoExpandAfterDays,
    [autoExpandAfterDays, daysSinceFirstLogin, defaultOpen],
  );
  const [isOpen, setIsOpen] = useState(defaultExpanded);
  const contentId = useId();

  return (
    <details
      className={cn("rounded-lg border border-border bg-muted/20", className)}
      open={isOpen}
      onToggle={(event) => setIsOpen(event.currentTarget.open)}
    >
      <summary
        className="flex cursor-pointer list-none items-start justify-between gap-3 px-4 py-3 text-left marker:hidden"
        aria-controls={contentId}
      >
        <div className="space-y-1">
          <div className="text-sm font-medium text-foreground">{title}</div>
          {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
        </div>
        <ChevronDown
          className={cn(
            "mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform",
            isOpen ? "rotate-180" : "",
          )}
          aria-hidden="true"
        />
      </summary>
      <div id={contentId} className={cn("border-t border-border px-4 py-4", contentClassName)}>
        {children}
      </div>
    </details>
  );
}
